import { AuthError } from '@nangohq/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EventType } from '@waitroom/analytics';
import { selectCurrentUserId } from '@waitroom/auth';
import {
  getConnectionsQueryKey,
  useIntegrations as useIntegrationsCommon,
  useUpdateGetConnectionsData,
} from '@waitroom/common';
import {
  DefaultApiResponse,
  IntegrationsApiService,
  integrationsApiService,
  RecallCalendarConnection,
  recallCalendarService,
} from '@waitroom/common-api';
import { logger } from '@waitroom/logger';
import { PostHogFeatureFlag } from '@waitroom/models';
import { getRequestData } from '@waitroom/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { analyticsService } from '../../analytics/services';
import { usePostHogFeatureFlag } from '../../core/components/FeatureFlagGate/FeatureFlag';
import { getClient } from '../../core/services/nango/client';
import { useSessionToast } from '../../session/hooks/useSessionToast';

export const useIntegrationDisconnect = () => {
  const queryClient = useQueryClient();
  const userId = useSelector(selectCurrentUserId);
  const toast = useSessionToast();

  return useMutation<
    IntegrationsApiService.Disconnect['response'],
    DefaultApiResponse,
    { params: IntegrationsApiService.Disconnect['params']; category: string }
  >({
    mutationFn: ({ params }) =>
      integrationsApiService.disconnect({
        params,
      }),
    onError: (err) => {
      toast(String(err.data?.message), 'error');
    },
    onSuccess: (_, { category, params }) => {
      // TODO: rather than invalidating, update the cache
      queryClient.invalidateQueries({
        queryKey: getConnectionsQueryKey(userId),
      });
      analyticsService.track(EventType.IntegrationRemoved, {
        integration: params.integrationName,
        category,
      });
    },
  });
};

type useIntegrationConnectProps = {
  onConnect?: () => void;
};

export const useIntegrationConnect = (props?: useIntegrationConnectProps) => {
  const toast = useSessionToast();
  const { onConnect: onConnectInternal } = useUpdateGetConnectionsData();

  const { mutate: onClickConnect, isPending: isConnecting } = useMutation({
    mutationFn: async ({
      integrationId,
      connectionId,
    }: {
      integrationId: string;
      connectionId: string;
    }) => {
      try {
        const res = await integrationsApiService.authorize({
          params: {
            integrationName: integrationId,
          },
        });
        const data = getRequestData(res);
        if (!data?.publicKey) {
          throw new Error('No publicKey found from getToken'); // TODO: more user friendly error message
        }
        const { publicKey, config } = data;
        const nango = getClient(publicKey);
        const result = await nango.auth(integrationId, connectionId, {
          ...config,
          detectClosedAuthWindow: true,
        });
        if (result instanceof AuthError) throw result;
        analyticsService.track(EventType.IntegrationAdded, {
          integration: integrationId,
        });
        await onConnectInternal();
        if (props?.onConnect) props.onConnect();
        return true;
      } catch (err) {
        if (
          err instanceof Error &&
          !err.message.includes('The authorization window was closed before')
        ) {
          logger.report(err);
        }

        toast((err as Error).message, 'error');
        return false;
      }
    },
  });

  return {
    onClickConnect,
    isConnecting,
  };
};

export const useIntegrations = (enabled?: boolean) => {
  const {
    //onClickConnect: onClickConnectCommon, // TODO: fix this
    refetchProviderCategories,
    ...common
  } = useIntegrationsCommon({
    isEnabled: enabled,
  });

  const { onClickConnect, isConnecting } = useIntegrationConnect({
    onConnect: refetchProviderCategories,
  });

  return {
    ...common,
    onClickConnect,
    isConnecting,
  };
};

export const useIsUnlistedCategory = () => {
  const isCRMEnabled = usePostHogFeatureFlag(PostHogFeatureFlag.CrmIntegrations);

  return (name: string) => name === 'crm' && !isCRMEnabled;
};

export const useRecallIntegration = () => {
  const toast = useSessionToast();
  const [connections, setConnections] = useState<RecallCalendarConnection[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    // Only fetch if the connections are empty
    if (connections.length !== 0) return;

    const getCalendarUser = async () => {
      try {
        const response = await recallCalendarService.getCalendarUser();
        setConnections(response?.connections || []);
      } catch (error) {
        toast(t('integrations.recallCalendar.errors.unableToGetConnections'), 'error');
        logger.log('Error while getting Recall calendar user', error);
      }
    };

    getCalendarUser();
  }, [connections.length, t, toast]);

  const handleConnectCalendar = useCallback(async () => {
    try {
      const response = await recallCalendarService.getGoogleAuthURL();
      // After auth the user is redirected to the backend, which then will save state and redirect to the frontend.
      // see backend/elio/transcriptions/recall.go for more details.
      window.location.href = response;
    } catch (error) {
      setConnections([]);
      toast(t('integrations.recallCalendar.errors.unableToConnect'), 'error');
      logger.log('Error while connecting to Recall calendar', error);
    }
  }, [t, toast]);

  const handleDisconnectCalendar = useCallback(async () => {
    try {
      await recallCalendarService.disconnectCalendar('google');
      setConnections([]);
    } catch (error) {
      toast(t('integrations.recallCalendar.errors.unableToDisconnect'), 'error');
      logger.log('Error while disconnecting from Recall calendar', error);
    }
  }, [t, toast]);

  return {
    connections,
    handleConnectCalendar,
    handleDisconnectCalendar,
  };
};
